import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2, X } from 'lucide-react';
import { Fragment, useEffect, useState, type CSSProperties } from 'react';
import type {
  MemberListItem,
  TrainerMonthStatus,
  TrainerRosterEntry,
  TrainerSessionSummary,
  TrainerSummaryRow,
  TrainingGroupResponse,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { downloadFile } from '../lib/file-download';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { DateBadge } from '../components/DateBadge';
import { GroupFilterDropdown, WeekdayFilterDropdown } from '../components/FilterDropdowns';
import { OrgTabs } from '../components/OrgTabs';
import { TrainerEditModal } from '../components/TrainerEditModal';
import { isoWeekday } from '../lib/weekday';

function formatAmount(amount: number): string {
  return `${amount.toFixed(2)} €`;
}

/** Deutsches Dezimalkomma statt Punkt für die kompakte Stundenangabe hinter
 * dem Trainer-Namen (§ Nutzeranfrage, z. B. „Tim 1,5") — bewusst ohne
 * `toFixed`, damit glatte Werte wie 2 nicht unnötig als „2,00" erscheinen. */
function formatHoursDe(hours: number): string {
  return String(hours).replace('.', ',');
}

/** Helles, farbiges Kapsel-Styling aus einem Hex-Wert (§ Nutzeranfrage) —
 * gleiches Prinzip wie das feste `bg-brand-accent/10`/`text-brand-accent` an
 * anderer Stelle, nur mit einer zur Laufzeit gewählten statt einer fest im
 * Theme hinterlegten Farbe, daher als Inline-Style statt Tailwind-Klasse. */
function trainerChipStyle(color: string | null): CSSProperties | undefined {
  if (!color) return undefined;
  return { backgroundColor: `${color}1a`, color };
}

function startOfYearIso(): string {
  return `${new Date().getFullYear()}-01-01`;
}
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}
function monthLabel(year: number, month: number): string {
  return new Date(year, month - 1, 1).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric',
  });
}

export function OrgTrainersPage() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const orgId = me?.activeOrganizationId ?? null;
  const [newTrainerId, setNewTrainerId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(startOfYearIso());
  const [to, setTo] = useState(todayIso());
  const [exporting, setExporting] = useState<'csv' | 'pdf' | null>(null);
  const [editingTrainer, setEditingTrainer] = useState<TrainerRosterEntry | null>(null);
  const [editingTrainerError, setEditingTrainerError] = useState<string | null>(null);
  const [sessionsMonth, setSessionsMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
  });
  const [sessionsGroupFilter, setSessionsGroupFilter] = useState<string[]>([]);
  const [sessionsWeekdayFilter, setSessionsWeekdayFilter] = useState<number[]>([]);
  const [deadlineDraft, setDeadlineDraft] = useState('');
  const [editingCheckInId, setEditingCheckInId] = useState<string | null>(null);
  const [editingHoursDraft, setEditingHoursDraft] = useState('');
  // § Nutzeranfrage „ich möchte auch selber Trainer für Trainingszeiten
  // eintragen können" — welche Einheit gerade ihr "+ Trainer"-Formular
  // offen hat, und dessen Entwurf.
  const [addingToEventId, setAddingToEventId] = useState<string | null>(null);
  const [addMemberId, setAddMemberId] = useState('');
  const [addHoursDraft, setAddHoursDraft] = useState('');

  const { data: roster } = useQuery({
    queryKey: ['trainers', 'roster'],
    queryFn: () => api<TrainerRosterEntry[]>('/trainers', { orgScoped: true }),
  });

  const { data: groups } = useQuery({
    queryKey: ['trainers', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });

  const { data: members } = useQuery({
    queryKey: ['trainers', 'members-for-roster'],
    queryFn: async () => {
      const page = await api<{ data: MemberListItem[] }>('/members?pageSize=200', {
        orgScoped: true,
      });
      return [...page.data].sort((a, b) =>
        `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, 'de'),
      );
    },
  });
  const rosterIds = new Set(roster?.map((r) => r.memberId) ?? []);
  const addableMembers = members?.filter((m) => !rosterIds.has(m.id)) ?? [];

  const { data: summary } = useQuery({
    queryKey: ['trainers', 'summary', from, to],
    queryFn: () =>
      api<TrainerSummaryRow[]>(`/trainers/summary?from=${from}&to=${to}`, { orgScoped: true }),
  });
  const { data: sessions } = useQuery({
    queryKey: ['trainers', 'sessions', sessionsMonth.year, sessionsMonth.month],
    queryFn: () =>
      api<TrainerSessionSummary[]>(
        `/trainers/sessions?year=${sessionsMonth.year}&month=${sessionsMonth.month}`,
        {
          orgScoped: true,
        },
      ),
  });
  const { data: monthStatus } = useQuery({
    queryKey: ['trainers', 'month-status', sessionsMonth.year, sessionsMonth.month],
    queryFn: () =>
      api<TrainerMonthStatus>(`/trainers/months/${sessionsMonth.year}/${sessionsMonth.month}`, {
        orgScoped: true,
      }),
  });
  useEffect(() => setDeadlineDraft(monthStatus?.deadline ?? ''), [monthStatus]);

  function shiftSessionsMonth(delta: number) {
    setSessionsMonth((m) => {
      const d = new Date(m.year, m.month - 1 + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() + 1 };
    });
  }

  // Nur Wochentage anbieten, an denen im gewählten Monat tatsächlich
  // Trainings stattfinden (§ Nutzeranfrage) — unabhängig vom aktuellen
  // Gruppen-/Wochentagfilter, damit sich die Optionsliste beim Filtern
  // nicht unter der Auswahl wegbewegt.
  const availableSessionWeekdays = [
    ...new Set((sessions ?? []).map((s) => isoWeekday(s.eventDate))),
  ].sort();
  const filteredSessions = sessions?.filter(
    (s) =>
      (sessionsGroupFilter.length === 0 ||
        s.trainingGroupIds.some((id) => sessionsGroupFilter.includes(id))) &&
      (sessionsWeekdayFilter.length === 0 ||
        sessionsWeekdayFilter.includes(isoWeekday(s.eventDate))),
  );
  // § Nutzeranfrage „moderner und übersichtlicher" — ein Datum nicht mehr je
  // Zeile wiederholen, sondern wie auf den anderen Listenseiten
  // (Training/Saison) einmal je Tag mit `DateBadge` links neben den
  // Trainings dieses Tages. `filteredSessions` kommt vom Server bereits nach
  // `eventDate` sortiert, daher reicht ein einfacher Lauf statt Gruppierung
  // nach Schlüssel.
  const sessionGroups: Array<{ date: string; sessions: TrainerSessionSummary[] }> = [];
  for (const s of filteredSessions ?? []) {
    const current = sessionGroups[sessionGroups.length - 1];
    if (current && current.date === s.eventDate) current.sessions.push(s);
    else sessionGroups.push({ date: s.eventDate, sessions: [s] });
  }
  // Roster-Mitglieder, die für eine Einheit noch NICHT eingetragen sind —
  // Auswahlliste des "+ Trainer"-Formulars.
  function eligibleTrainersFor(s: TrainerSessionSummary): TrainerRosterEntry[] {
    const already = new Set(s.trainers.map((t) => t.memberId));
    return (roster ?? []).filter((t) => !already.has(t.memberId));
  }

  const addTrainer = useMutation({
    mutationFn: (memberId: string) =>
      api('/trainers', { method: 'POST', orgScoped: true, body: { memberId } }),
    onSuccess: () => {
      setNewTrainerId('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'roster'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Hinzufügen fehlgeschlagen.'),
  });

  const removeTrainer = useMutation({
    mutationFn: (memberId: string) =>
      api(`/trainers/${memberId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['trainers', 'roster'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Entfernen fehlgeschlagen.'),
  });

  const updateTrainer = useMutation({
    mutationFn: ({
      memberId,
      rates,
      color,
    }: {
      memberId: string;
      rates: Array<{ trainingGroupId: string; hourlyRate: number }>;
      color: string | null;
    }) => api(`/trainers/${memberId}`, { method: 'PUT', orgScoped: true, body: { rates, color } }),
    onSuccess: () => {
      setEditingTrainer(null);
      setEditingTrainerError(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'roster'] });
      void qc.invalidateQueries({ queryKey: ['trainers', 'sessions'] });
    },
    onError: (e) =>
      setEditingTrainerError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const setMonthStatus = useMutation({
    mutationFn: (patch: { deadline?: string | null; closed?: boolean }) =>
      api<TrainerMonthStatus>(`/trainers/months/${sessionsMonth.year}/${sessionsMonth.month}`, {
        method: 'PUT',
        orgScoped: true,
        body: patch,
      }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'month-status'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const removeCheckIn = useMutation({
    mutationFn: (id: string) =>
      api(`/trainers/checkins/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      setEditingCheckInId(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['trainers', 'sessions'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const updateCheckIn = useMutation({
    mutationFn: ({ id, hours }: { id: string; hours: number }) =>
      api(`/trainers/checkins/${id}`, { method: 'PUT', orgScoped: true, body: { hours } }),
    onSuccess: () => {
      setEditingCheckInId(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['trainers', 'sessions'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  // § Nutzeranfrage „ich möchte … auch selber Trainer für Trainingszeiten
  // eintragen können" — Admin-Pendant zur Selbst-Eintragung auf der
  // Trainer-Arbeitsseite, hier direkt aus der Trainings-Übersicht heraus.
  const addCheckIn = useMutation({
    mutationFn: ({
      eventId,
      memberId,
      hours,
    }: {
      eventId: string;
      memberId: string;
      hours: number;
    }) =>
      api(`/trainers/events/${eventId}/checkins`, {
        method: 'POST',
        orgScoped: true,
        body: { memberId, hours },
      }),
    onSuccess: () => {
      setAddingToEventId(null);
      setAddMemberId('');
      setAddHoursDraft('');
      setError(null);
      void qc.invalidateQueries({ queryKey: ['trainers', 'summary'] });
      void qc.invalidateQueries({ queryKey: ['trainers', 'sessions'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Eintragen fehlgeschlagen.'),
  });

  function startEditingCheckIn(checkInId: string, hours: number) {
    setAddingToEventId(null);
    setEditingCheckInId(checkInId);
    setEditingHoursDraft(String(hours));
  }

  function submitEditingCheckIn() {
    if (!editingCheckInId) return;
    const raw = editingHoursDraft.replace(',', '.').trim();
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte eine gültige Stundenzahl eingeben.');
      return;
    }
    updateCheckIn.mutate({ id: editingCheckInId, hours: parsed });
  }

  function startAddingTrainer(s: TrainerSessionSummary) {
    setEditingCheckInId(null);
    setAddingToEventId(s.id);
    setAddMemberId('');
    setAddHoursDraft(String(s.computedHours));
  }

  function submitAddingTrainer(eventId: string) {
    if (!addMemberId) {
      setError('Bitte einen Trainer auswählen.');
      return;
    }
    const raw = addHoursDraft.replace(',', '.').trim();
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte eine gültige Stundenzahl eingeben.');
      return;
    }
    addCheckIn.mutate({ eventId, memberId: addMemberId, hours: parsed });
  }

  async function handleExport(format: 'csv' | 'pdf') {
    setExporting(format);
    setError(null);
    try {
      await downloadFile(
        `/trainers/export.${format}?from=${from}&to=${to}`,
        `trainer-abrechnung_${from}_${to}.${format}`,
        orgId,
      );
    } catch {
      setError('Export fehlgeschlagen.');
    } finally {
      setExporting(null);
    }
  }

  const totalHours = summary?.reduce((sum, r) => sum + r.totalHours, 0) ?? 0;
  const totalAmount = summary?.reduce((sum, r) => sum + r.totalAmount, 0) ?? 0;
  const totalUnrated = summary?.reduce((sum, r) => sum + r.unratedSessionCount, 0) ?? 0;

  /** Trainer-Kapseln + Bearbeiten-/Eintragen-Formulare einer Einheit — als
   * Funktion statt Inline-JSX ausgelagert, weil exakt dieselbe (recht
   * komplexe) Inline-Bearbeitung sowohl in der Desktop-Grid-Zelle als auch
   * in der mobilen Karten-Ansicht (§ Nutzeranfrage „restliche Seiten … in
   * der mobilen Ansicht anpassen", siehe unten) gebraucht wird — eine Kopie
   * hätte beide Stellen bei jeder künftigen Änderung auseinanderlaufen
   * lassen können. */
  function renderTrainerControls(s: TrainerSessionSummary) {
    return (
      <>
        <div className="flex flex-wrap items-center gap-1">
          {s.trainers.map((t) =>
            editingCheckInId === t.checkInId ? (
              <span
                key={t.checkInId}
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2 pr-1"
              >
                <span className="text-xs text-slate-600">{t.firstName}</span>
                <input
                  type="number"
                  step="0.25"
                  min="0"
                  max="24"
                  autoFocus
                  aria-label="Stunden"
                  className="w-12 rounded border border-slate-300 px-1 py-0.5 text-right text-xs"
                  value={editingHoursDraft}
                  onChange={(e) => setEditingHoursDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitEditingCheckIn();
                    if (e.key === 'Escape') setEditingCheckInId(null);
                  }}
                />
                <button
                  type="button"
                  title="Speichern"
                  disabled={updateCheckIn.isPending}
                  onClick={submitEditingCheckIn}
                  className="rounded p-0.5 text-brand-accent hover:bg-white"
                >
                  <Check size={13} />
                </button>
                <button
                  type="button"
                  title="Eintragung löschen"
                  disabled={removeCheckIn.isPending}
                  onClick={() => {
                    if (confirm(`Eintragung von ${t.firstName} wirklich löschen?`)) {
                      removeCheckIn.mutate(t.checkInId);
                    }
                  }}
                  className="rounded p-0.5 text-red-600 hover:bg-white"
                >
                  <Trash2 size={13} />
                </button>
                <button
                  type="button"
                  title="Abbrechen"
                  onClick={() => setEditingCheckInId(null)}
                  className="rounded p-0.5 text-slate-400 hover:bg-white"
                >
                  <X size={13} />
                </button>
              </span>
            ) : (
              <button
                key={t.checkInId}
                type="button"
                title={`${t.hours.toFixed(2)} Std. — zum Korrigieren klicken`}
                onClick={() => startEditingCheckIn(t.checkInId, t.hours)}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 transition hover:ring-1 hover:ring-slate-300"
                style={trainerChipStyle(t.color)}
              >
                {t.firstName} {formatHoursDe(t.hours)}
              </button>
            ),
          )}
        </div>
        {addingToEventId === s.id ? (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-dashed border-slate-300 py-0.5 pl-2 pr-1">
            <select
              autoFocus
              aria-label="Trainer auswählen"
              className="rounded border border-slate-300 bg-white px-1 py-0.5 text-xs text-slate-700"
              value={addMemberId}
              onChange={(e) => setAddMemberId(e.target.value)}
            >
              <option value="">Trainer wählen …</option>
              {eligibleTrainersFor(s).map((t) => (
                <option key={t.memberId} value={t.memberId}>
                  {t.firstName} {t.lastName}
                </option>
              ))}
            </select>
            <input
              type="number"
              step="0.25"
              min="0"
              max="24"
              aria-label="Stunden"
              className="w-12 rounded border border-slate-300 px-1 py-0.5 text-right text-xs"
              value={addHoursDraft}
              onChange={(e) => setAddHoursDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submitAddingTrainer(s.id);
                if (e.key === 'Escape') setAddingToEventId(null);
              }}
            />
            <button
              type="button"
              title="Eintragen"
              disabled={addCheckIn.isPending}
              onClick={() => submitAddingTrainer(s.id)}
              className="rounded p-0.5 text-brand-accent hover:bg-white"
            >
              <Check size={13} />
            </button>
            <button
              type="button"
              title="Abbrechen"
              onClick={() => setAddingToEventId(null)}
              className="rounded p-0.5 text-slate-400 hover:bg-white"
            >
              <X size={13} />
            </button>
          </span>
        ) : s.inFuture ? (
          s.trainers.length === 0 && <span className="text-xs text-slate-400">Noch kein Trainer</span>
        ) : (
          <button
            type="button"
            title="Trainer eintragen"
            onClick={() => startAddingTrainer(s)}
            className="ml-auto inline-flex items-center gap-0.5 rounded-full border border-dashed border-slate-300 px-2 py-0.5 text-xs text-slate-500 transition hover:border-brand-accent hover:text-brand-accent"
          >
            <Plus size={12} /> Trainer
          </button>
        )}
      </>
    );
  }

  function renderHoursSummary(s: TrainerSessionSummary) {
    return (
      <>
        {s.totalHours.toFixed(2)} Std.
        {s.totalAmount > 0 && ` · ${formatAmount(s.totalAmount)}`}
        {s.unratedCount > 0 && (
          <span className="ml-1 text-amber-600" title={`${s.unratedCount} Eintragung(en) ohne hinterlegten Satz`}>
            *
          </span>
        )}
      </>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />

      {error && <Alert>{error}</Alert>}

      <Card title="Trainer">
        <p className="mb-3 text-xs text-slate-400">
          Wer hier eingetragen ist, kann sich auf der Trainer-Seite (unter „Verwaltung") in
          Trainingseinheiten eintragen. Muss ein bestehendes Mitglied sein.
        </p>
        {roster && roster.length > 0 ? (
          <ul className="mb-4 divide-y divide-slate-100">
            {roster.map((t) => (
              <li key={t.memberId} className="flex items-center justify-between gap-2 py-2 text-sm">
                <div>
                  <span>
                    {t.firstName} {t.lastName}
                  </span>
                  {t.rates.length > 0 ? (
                    <p className="mt-0.5 text-xs text-slate-400">
                      {t.rates
                        .map((r) => `${r.trainingGroupName}: ${formatAmount(r.hourlyRate)}/Std`)
                        .join(' · ')}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-xs text-slate-400">Kein Stundensatz hinterlegt</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs"
                    onClick={() => {
                      setEditingTrainerError(null);
                      setEditingTrainer(t);
                    }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                    disabled={removeTrainer.isPending}
                    onClick={() => {
                      if (confirm(`${t.firstName} ${t.lastName} nicht mehr als Trainer führen?`)) {
                        removeTrainer.mutate(t.memberId);
                      }
                    }}
                  >
                    Entfernen
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-4 text-sm text-slate-500">Noch keine Trainer angelegt.</p>
        )}

        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (newTrainerId) addTrainer.mutate(newTrainerId);
          }}
        >
          <select
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
            value={newTrainerId}
            onChange={(e) => setNewTrainerId(e.target.value)}
          >
            <option value="">Mitglied auswählen …</option>
            {addableMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>
          <Button type="submit" variant="ghost" disabled={!newTrainerId || addTrainer.isPending}>
            Als Trainer hinzufügen
          </Button>
        </form>
      </Card>

      <Card title="Trainings-Übersicht">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-slate-400">
            Trainings des Monats mit den eingetragenen Trainern — auf eine Eintragung klicken, um
            sie zu korrigieren oder zu löschen, mit „+ Trainer" selbst einen eintragen.
          </p>
          <div className="flex shrink-0 items-center gap-1 text-sm">
            <button
              type="button"
              aria-label="Vorheriger Monat"
              onClick={() => shiftSessionsMonth(-1)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              ‹
            </button>
            <span className="min-w-28 text-center font-medium text-slate-700">
              {monthLabel(sessionsMonth.year, sessionsMonth.month)}
            </span>
            <button
              type="button"
              aria-label="Nächster Monat"
              onClick={() => shiftSessionsMonth(1)}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              ›
            </button>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
          {monthStatus?.closedAt ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
              Geschlossen seit {formatDate(monthStatus.closedAt.slice(0, 10))}
            </span>
          ) : (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
              Offen
            </span>
          )}
          <span className="text-xs text-slate-500">Deadline für Vormonat-Stunden:</span>
          <Input
            type="date"
            className="w-auto py-1 text-xs"
            value={deadlineDraft}
            onChange={(e) => setDeadlineDraft(e.target.value)}
          />
          <Button
            type="button"
            variant="ghost"
            className="px-2 py-1 text-xs"
            disabled={setMonthStatus.isPending || deadlineDraft === (monthStatus?.deadline ?? '')}
            onClick={() =>
              setMonthStatus.mutate({ deadline: deadlineDraft === '' ? null : deadlineDraft })
            }
          >
            Deadline speichern
          </Button>
          <Button
            type="button"
            variant={monthStatus?.closedAt ? 'ghost' : 'danger'}
            className="ml-auto px-2 py-1 text-xs"
            disabled={setMonthStatus.isPending}
            onClick={() => setMonthStatus.mutate({ closed: !monthStatus?.closedAt })}
          >
            {monthStatus?.closedAt ? 'Wieder öffnen' : 'Monat schließen'}
          </Button>
        </div>

        <div className="mb-2 flex flex-wrap items-center gap-2">
          <GroupFilterDropdown
            groups={groups ?? []}
            selected={sessionsGroupFilter}
            onChange={setSessionsGroupFilter}
          />
          <WeekdayFilterDropdown
            selected={sessionsWeekdayFilter}
            onChange={setSessionsWeekdayFilter}
            availableDays={availableSessionWeekdays}
          />
        </div>

        {sessionGroups.length > 0 ? (
          // § Nutzeranfrage „soll in Spalten angezeigt werden" — ein einziges
          // Grid über die komplette Liste (nicht ein Grid je Tag), damit
          // Zeit-/Gruppen-/Trainer-/Stunden-Spalte über alle Tage hinweg exakt
          // auf derselben Breite stehen, inkl. einer Kopfzeile. Jede Zeile ist
          // ein `display: contents`-Wrapper (kein eigener Box, nur
          // Gruppierung für den React-`key`), dessen Kind-Zellen dadurch
          // direkt als Grid-Items des äußeren Grids zählen. Auf schmalen
          // Bildschirmen reicht die Breite für fünf Spalten nicht —
          // `overflow-x-auto` + `min-w` auf dem Grid selbst (statt die
          // Spalten zu stauchen) macht die Liste dort horizontal scrollbar,
          // analog zu den bestehenden Tabellen in MembersPage/SeasonPage.
          //
          // Spalten leicht eingefärbt (§ Nutzeranfrage) — Zeit/Trainer
          // bekommen ein dezentes `bg-slate-50`, Gruppe/Stunden bleiben weiß,
          // macht die Spalten auf einen Blick auseinanderhaltbar, ohne mit
          // den ohnehin schon farbigen Gruppen-/Trainer-Kapseln zu
          // konkurrieren. Die Datums-Spalte bleibt eigens ungefärbt — die
          // `DateBadge` hat bereits eine eigene, kräftigere Optik.
          //
          // Trainingsgruppen einer Einheit stehen nebeneinander
          // (§ Nutzeranfrage) in der Gruppe-Zelle — eine Einheit für z. B.
          // „JTG1, JTG2" gemeinsam zeigt zwei Kapseln in einer Zeile
          // (`flex flex-wrap gap-1`), nicht als kommagetrennter Text und
          // nicht als separate Zeilen (eine frühere Fassung stapelte sie
          // vertikal — laut Rückmeldung nicht gewünscht).
          //
          // Die `DateBadge` ist kein Teil einer einzelnen Zeile, sondern ein
          // eigenes Grid-Item direkt nach dem Tages-Trenner, mit
          // `gridRow: span <Anzahl Einheiten des Tages>` und `self-center`
          // — sie steht dadurch vertikal zentriert neben dem ganzen
          // Tagesblock, statt (wie ursprünglich) die erste Zeile auf ihre
          // eigene Höhe zu zwingen und darunter eine Lücke zu hinterlassen.
          // Die übrigen Zeilen des Tages lassen die Datums-Spalte dafür
          // komplett aus — das CSS-Grid platziert ihre vier Zellen
          // automatisch ab Spalte 2, da Spalte 1 für die Dauer der
          // `DateBadge`-Spanne belegt ist.
          <>
          <div className="hidden overflow-x-auto md:block">
            <div className="grid min-w-[34rem] grid-cols-[2.75rem_3rem_minmax(5.5rem,auto)_1fr_auto] items-start gap-x-3 text-sm">
              <span />
              <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Zeit
              </span>
              <span className="rounded-md px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Gruppe
              </span>
              <span className="rounded-md bg-slate-50 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Trainer
              </span>
              <span className="rounded-md px-2 py-1 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Stunden
              </span>
              <div className="col-span-5 mb-1.5 border-b border-slate-200" />

              {sessionGroups.map((group, groupIndex) => {
                const dayRowCount = group.sessions.length;
                return (
                  <Fragment key={group.date}>
                    {groupIndex > 0 && (
                      <div className="col-span-5 my-1.5 border-t border-slate-100" />
                    )}
                    <div className="self-center" style={{ gridRow: `span ${dayRowCount}` }}>
                      <DateBadge date={group.date} />
                    </div>
                    {group.sessions.map((s) => (
                      <div key={s.id} className="contents">
                        <div className="rounded-md bg-slate-50 px-2 pb-1.5 pt-1.5 text-xs text-slate-400">
                          {s.startTime.slice(0, 5)}
                        </div>
                        <div className="rounded-md px-2 pb-1.5 pt-1">
                          <div className="flex flex-wrap gap-1">
                            {s.trainingGroupNames.map((name) => (
                              <span
                                key={name}
                                className="inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                              >
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="rounded-md bg-slate-50 px-2 pb-1.5 py-0.5">
                          <div className="flex flex-wrap items-center gap-1">{renderTrainerControls(s)}</div>
                        </div>
                        <div className="rounded-md px-2 pb-1.5 pt-1 text-right text-xs text-slate-500">
                          {renderHoursSummary(s)}
                        </div>
                      </div>
                    ))}
                  </Fragment>
                );
              })}
            </div>
          </div>

          {/* Mobile (`md:hidden`): das Grid oben erzwingt mit `min-w-[34rem]`
           * horizontales Scrollen, auf dem dann fast nur Zeit/Gruppe sichtbar
           * blieben und Trainer-Kapseln/Eintragen-Formulare verschnitten
           * wirkten (§ Nutzeranfrage „restliche Seiten … in der mobilen
           * Ansicht anpassen"). Hier stattdessen je Tag eine `DateBadge`-
           * Kopfzeile, darunter jede Einheit als eigener, leicht hinterlegter
           * Block mit Zeit+Gruppen-Kapseln und Stunden-Summe in einer Zeile,
           * den Trainer-Kapseln/Formularen darunter — dieselbe
           * `renderTrainerControls`/`renderHoursSummary`-Logik wie im Grid
           * oben, nur gestapelt statt nebeneinander. */}
          <div className="divide-y divide-slate-100 md:hidden">
            {sessionGroups.map((group) => (
              <div key={group.date} className="py-3 first:pt-0">
                <div className="mb-2">
                  <DateBadge date={group.date} />
                </div>
                <div className="space-y-2">
                  {group.sessions.map((s) => (
                    <div key={s.id} className="rounded-lg bg-slate-50 p-2.5">
                      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-xs font-medium text-slate-500">{s.startTime.slice(0, 5)}</span>
                          {s.trainingGroupNames.map((name) => (
                            <span
                              key={name}
                              className="inline-block rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                            >
                              {name}
                            </span>
                          ))}
                        </div>
                        <span className="shrink-0 text-xs text-slate-500">{renderHoursSummary(s)}</span>
                      </div>
                      <div className="flex flex-wrap items-center gap-1">{renderTrainerControls(s)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          </>
        ) : sessions && sessions.length > 0 ? (
          <p className="text-sm text-slate-500">
            Keine Trainings für die gewählten Filter.{' '}
            <button
              type="button"
              className="text-brand-accent hover:underline"
              onClick={() => {
                setSessionsGroupFilter([]);
                setSessionsWeekdayFilter([]);
              }}
            >
              Filter zurücksetzen
            </button>
          </p>
        ) : (
          <p className="text-sm text-slate-500">Keine Trainings in diesem Monat.</p>
        )}
      </Card>

      <Card title="Stundenauswertung">
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <Field label="Von">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Bis">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Button
            type="button"
            variant="ghost"
            disabled={exporting === 'csv' || !summary?.length}
            onClick={() => void handleExport('csv')}
          >
            {exporting === 'csv' ? 'Exportiert …' : 'Als CSV exportieren'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            disabled={exporting === 'pdf' || !summary?.length}
            onClick={() => void handleExport('pdf')}
          >
            {exporting === 'pdf' ? 'Exportiert …' : 'Als PDF exportieren'}
          </Button>
        </div>

        {summary && summary.length > 0 ? (
          <>
            {/* Nur 4 schmale Spalten — auf Mobile keine Kartenliste nötig wie
             * bei der Trainings-Übersicht oben, ein defensiver
             * `overflow-x-auto` reicht als Sicherheitsnetz für sehr lange
             * Namen statt eines harten Überlaufs. */}
            <div className="overflow-x-auto">
            <table className="mb-2 w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-1.5">Trainer</th>
                  <th className="py-1.5 text-right">Einheiten</th>
                  <th className="py-1.5 text-right">Stunden</th>
                  <th className="py-1.5 text-right">Betrag</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {summary.map((r) => (
                  <tr key={r.memberId ?? r.memberName}>
                    <td className="py-1.5">{r.memberName}</td>
                    <td className="py-1.5 text-right">{r.sessionCount}</td>
                    <td className="py-1.5 text-right">{r.totalHours.toFixed(2)}</td>
                    <td className="py-1.5 text-right">
                      {formatAmount(r.totalAmount)}
                      {r.unratedSessionCount > 0 && (
                        <span
                          className="ml-1 text-xs text-amber-600"
                          title="Ohne hinterlegten Satz, nicht in der Summe enthalten"
                        >
                          ({r.unratedSessionCount}×ohne Satz)
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 font-medium">
                  <td className="py-1.5">Gesamt</td>
                  <td></td>
                  <td className="py-1.5 text-right">{totalHours.toFixed(2)}</td>
                  <td className="py-1.5 text-right">{formatAmount(totalAmount)}</td>
                </tr>
              </tfoot>
            </table>
            </div>
            {totalUnrated > 0 && (
              <p className="text-xs text-amber-600">
                {totalUnrated} Eintragung(en) ohne hinterlegten Stundensatz — nicht in „Betrag"
                enthalten (z. B. Einheiten ohne oder mit mehreren Trainingsgruppen, oder Satz erst
                nachträglich hinterlegt).
              </p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">Keine Eintragungen im gewählten Zeitraum.</p>
        )}
      </Card>

      {editingTrainer && (
        <TrainerEditModal
          trainer={editingTrainer}
          groups={groups ?? []}
          onClose={() => setEditingTrainer(null)}
          onSave={(rates, color) =>
            updateTrainer.mutate({ memberId: editingTrainer.memberId, rates, color })
          }
          saving={updateTrainer.isPending}
          error={editingTrainerError}
        />
      )}
    </div>
  );
}
